/**
 * "Se a IA parar por limite, a próxima assume" (the owner's ask, 15/10): which of this machine's
 * other agents take a request when the main one runs out of its plan, and in which order. Ticked
 * agents are the order, numbered; "antes" moves one a step earlier. The free local AI can be last.
 */

import { ArrowUp } from 'lucide-react'
import { useI18n } from '../lib/i18n'
import type { LocalAgentId } from '../lib/localAgent'

export function FailoverOrder({
  main,
  available,
  value,
  onChange,
}: {
  main: LocalAgentId
  available: Array<{ id: LocalAgentId; name: string }>
  value: LocalAgentId[]
  onChange: (next: LocalAgentId[]) => void
}) {
  const { t } = useI18n()
  const others = available.filter((a) => a.id !== main)
  const order = value.filter((id) => others.some((a) => a.id === id))
  // The order first, as numbered; the rest after, unticked.
  const rows = [...order.map((id) => others.find((a) => a.id === id)!), ...others.filter((a) => !order.includes(a.id))]

  if (others.length === 0) return <span className="tiny muted">{t('failover.none')}</span>

  return (
    <div className="stack stack--tight failover">
      <span className="field__label">{t('failover.title')}</span>
      <span className="tiny muted">{t('failover.hint')}</span>
      {rows.map((agent) => {
        const at = order.indexOf(agent.id)
        return (
          <div key={agent.id} className="row row--tight failover__row">
            <label className="row row--tight">
              <input
                type="checkbox"
                checked={at >= 0}
                onChange={() => onChange(at >= 0 ? order.filter((id) => id !== agent.id) : [...order, agent.id])}
              />
              <span className="small">{at >= 0 ? `${at + 1}. ` : ''}{agent.name}</span>
            </label>
            {at > 0 && (
              <button
                type="button"
                className="btn btn--ghost btn--sm btn--icon"
                aria-label={t('failover.earlier', { name: agent.name })}
                title={t('failover.earlier', { name: agent.name })}
                onClick={() => {
                  const next = [...order]
                  next.splice(at, 1)
                  next.splice(at - 1, 0, agent.id)
                  onChange(next)
                }}
              >
                <ArrowUp size={13} />
              </button>
            )}
          </div>
        )
      })}
    </div>
  )
}
